import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import type { RootState } from "../../../store/store";

import type {
  StoreRegistrationItem,
  StoreSlotWithAvailability,
} from "../../../views/scheduling/schedulingApi";
import {
  listStoreSlotsWithTokenApi,
  substituteRegistrationApi,
  SubstituteRegistrationDto,
} from "../../../views/scheduling/schedulingApi";

import {
  getFreelancersPaginatedApi,
  FreelancerPublic,
} from "../../../views/freelancer/freelancerApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;

  storeId: string;
  registration: StoreRegistrationItem | null;

  onSuccess?: () => void;
};

const LIMIT = 10;

type Step = "SELECT" | "SLOT";
type SlotMode = "EXISTING" | "CREATE";

function formatTimeFromIso(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

function safeText(s?: string | null) {
  return (s ?? "").trim();
}

function toYmdFromAny(input?: string | Date | null): string {
  if (!input) return "";
  if (typeof input === "string") return input.slice(0, 10);
  return input.toISOString().slice(0, 10);
}

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = (ymd ?? "").split("-");
  if (!y || !m || !d) return ymd ?? "";
  return `${d}/${m}/${y}`;
}

function minutesFromHHMM(hhmm: string): number {
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

function hhmmFromMinutes(totalMinutes: number): string {
  const mins = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function buildIsoUtc(dayYmd: string, hhmm: string): string {
  return `${dayYmd}T${hhmm}:00-03:00`;
}

const SubstituteFreelancerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  storeId,
  registration,
  onSuccess,
}) => {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [step, setStep] = useState<Step>("SELECT");
  const [slotMode, setSlotMode] = useState<SlotMode>("EXISTING");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const [items, setItems] = useState<FreelancerPublic[]>([]);
  const [selectedFreelancerId, setSelectedFreelancerId] = useState<string>("");

  const [slotsLoading, setSlotsLoading] = useState(false);
  const slotsRequestIdRef = useRef(0);
  const [slotItems, setSlotItems] = useState<StoreSlotWithAvailability[]>([]);
  const [selectedTargetSlotId, setSelectedTargetSlotId] = useState<string>("");

  const [saving, setSaving] = useState(false);

  const currentFreelancerId = registration?.freelancer?.id ?? "";
  const currentFreelancerName = registration?.freelancer?.nomeCompleto ?? "";
  const currentSlotId = registration?.slot?.id ?? "";

  const slotLabel = useMemo(() => {
    if (!registration?.slot) return "-";
    const t1 = formatTimeFromIso(registration.slot.startAt);
    const t2 = formatTimeFromIso(registration.slot.endAt);
    return `${t1}-${t2}`;
  }, [registration]);

  const dayYmd = useMemo(() => {
    const d =
      (registration as any)?.day ??
      (registration as any)?.slot?.day ??
      (registration as any)?.slot?.startAt ??
      null;
    return toYmdFromAny(d);
  }, [registration]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / LIMIT)), [total]);

  const filtered = useMemo(() => {
    return (items ?? []).filter((f) => f.id !== currentFreelancerId);
  }, [items, currentFreelancerId]);

  const [searchApplied, setSearchApplied] = useState("");

  const fetchPage = useCallback(
    async (p: number) => {
      if (!token) return;

      const reqId = (requestIdRef.current += 1);

      try {
        setLoading(true);

        const q = safeText(searchApplied);

        const data = await getFreelancersPaginatedApi(token, {
          page: p,
          limit: LIMIT,
          ...(q ? { nome: q } : {}),
        });

        if (reqId !== requestIdRef.current) return;

        setItems(data.items ?? []);
        setTotal(data.total ?? 0);
      } catch {
        if (reqId !== requestIdRef.current) return;

        toast.error("Erro ao listar freelancers.");
        setItems([]);
        setTotal(0);
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    },
    [token, searchApplied]
  );

  const fetchSlots = useCallback(async () => {
    if (!token || !storeId || !dayYmd) return;

    const reqId = (slotsRequestIdRef.current += 1);

    try {
      setSlotsLoading(true);

      const data = await listStoreSlotsWithTokenApi(storeId, token, {
        day: dayYmd,
        includeCanceled: true,
      });

      if (reqId !== slotsRequestIdRef.current) return;

      setSlotItems(data ?? []);
    } catch {
      if (reqId !== slotsRequestIdRef.current) return;
      toast.error("Erro ao listar horários da loja.");
      setSlotItems([]);
    } finally {
      if (reqId === slotsRequestIdRef.current) setSlotsLoading(false);
    }
  }, [token, storeId, dayYmd]);

  const currentSlotStartIso = registration?.slot?.startAt ?? "";

  const defaultStartTime = useMemo(() => {
    if (!currentSlotStartIso) return "18:00";
    const t = formatTimeFromIso(currentSlotStartIso) || "18:00";
    return hhmmFromMinutes(minutesFromHHMM(t) + 60);
  }, [currentSlotStartIso]);

  const defaultEndTime = useMemo(() => {
    const t = defaultStartTime || "18:00";
    return hhmmFromMinutes(minutesFromHHMM(t) + 9 * 60);
  }, [defaultStartTime]);

  const [newStartTime, setNewStartTime] = useState<string>("18:00");
  const [newEndTime, setNewEndTime] = useState<string>("21:00");

  const selectedFreelancerName = useMemo(() => {
    const f = filtered.find((x) => x.id === selectedFreelancerId);
    return f?.nomeCompleto ?? "";
  }, [filtered, selectedFreelancerId]);

  const selectedTargetSlot = useMemo(() => {
    return slotItems.find((slot) => slot.id === selectedTargetSlotId) ?? null;
  }, [slotItems, selectedTargetSlotId]);

  const busy = loading || slotsLoading || saving;

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    setStep("SELECT");
    setSlotMode("EXISTING");

    setSearch("");
    setSearchApplied("");
    setSelectedFreelancerId("");
    setItems([]);
    setTotal(0);
    setPage(1);

    setSlotItems([]);
    setSelectedTargetSlotId(currentSlotId);

    setNewStartTime(defaultStartTime);
    setNewEndTime(defaultEndTime);
  }, [isOpen, currentSlotId, defaultStartTime, defaultEndTime]);

  useEffect(() => {
    if (!isOpen) return;
    fetchPage(page);
  }, [isOpen, page, fetchPage]);

  useEffect(() => {
    if (!isOpen || step !== "SLOT") return;
    fetchSlots();
  }, [isOpen, step, fetchSlots]);

  useEffect(() => {
    if (step !== "SLOT") return;
    if (!slotItems.length) return;

    if (slotItems.some((slot) => slot.id === selectedTargetSlotId)) return;

    const current = slotItems.find((slot) => slot.id === currentSlotId);
    if (current) {
      setSelectedTargetSlotId(current.id);
      return;
    }

    setSelectedTargetSlotId(slotItems[0].id);
  }, [step, slotItems, selectedTargetSlotId, currentSlotId]);

  const handleOverlayClick = () => {
    if (!saving) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => e.stopPropagation();

  const handleSearch = () => {
    const q = safeText(search);
    setSearchApplied(q);
    setPage(1);
  };

  const handleGoToSlotStep = () => {
    if (!selectedFreelancerId) {
      toast.info("Selecione um freelancer para substituir.");
      return;
    }

    setStep("SLOT");
  };

  const validateNewSlot = () => {
    if (!dayYmd) {
      toast.error("Dia inválido para criação do novo horário.");
      return false;
    }

    const sMin = minutesFromHHMM(newStartTime);
    const eMin = minutesFromHHMM(newEndTime);

    if (Number.isNaN(sMin) || Number.isNaN(eMin)) {
      toast.error("Horário inválido.");
      return false;
    }

    if (sMin % 30 !== 0 || eMin % 30 !== 0) {
      toast.error("Use horários de meia em meia hora.");
      return false;
    }

    if (eMin === sMin) {
      toast.error("O fim deve ser diferente do início.");
      return false;
    }

    return true;
  };

  const handleConfirm = async () => {
    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.");
      onClose();
      return;
    }

    if (!registration?.id) return;

    if (!selectedFreelancerId) {
      toast.info("Selecione um freelancer para substituir.");
      setStep("SELECT");
      return;
    }

    let dto: SubstituteRegistrationDto;

    if (slotMode === "EXISTING") {
      if (!selectedTargetSlotId) {
        toast.info("Selecione um horário de destino.");
        return;
      }

      dto = {
        newFreelancerId: selectedFreelancerId,
        targetSlotId: selectedTargetSlotId,
      };
    } else {
      if (!validateNewSlot()) return;

      const sMin = minutesFromHHMM(newStartTime);
      const eMin = minutesFromHHMM(newEndTime);
      const endDayYmd = eMin > sMin ? dayYmd : addDaysYmd(dayYmd, 1);

      dto = {
        newFreelancerId: selectedFreelancerId,
        createSlot: {
          startAt: buildIsoUtc(dayYmd, newStartTime),
          endAt: buildIsoUtc(endDayYmd, newEndTime),
        },
      };
    }

    try {
      setSaving(true);

      const res = await substituteRegistrationApi(registration.id, token, dto);

      const successMsg =
        (res as any)?.message ??
        (res as any)?.data?.message ??
        "Substituição realizada com sucesso.";

      toast.success(successMsg);
      onClose();
      onSuccess?.();
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ??
        err?.data?.message ??
        err?.message ??
        "Erro ao substituir freelancer. Verifique permissões e dados.";

      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">Substituir freelancer</h2>

        {step === "SELECT" && (
          <div className="ModalBody">
            <div className="SelectedInfoBox">
              <div className="SelectedInfoRow">
                <span className="SelectedInfoLabel">Horário atual</span>
                <span className="SelectedInfoValue">
                  {dayYmd ? `${formatBrDate(dayYmd)} • ${slotLabel}` : slotLabel}
                </span>
              </div>

              <div className="SelectedInfoRow">
                <span className="SelectedInfoLabel">Atual</span>
                <span className="SelectedInfoValue">{currentFreelancerName || "-"}</span>
              </div>

              <div className="PVHint" style={{ marginTop: 10 }}>
                A inscrição atual será marcada como <b>CANCELADO</b> e o novo freelancer entrará como{" "}
                <b>PENDENTE</b>.
              </div>
            </div>

            <div className="Box">
              <div className="BoxTitle">Selecione o novo freelancer</div>

              <div className="PresenceValuesTopRow">
                <div className="PVField" style={{ flex: 1 }}>
                  <label className="PVLabel">Buscar por nome</label>
                  <input
                    className="PVInput"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Ex.: João"
                    disabled={busy}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                  />
                </div>

                <button
                  type="button"
                  className="PagerButton"
                  onClick={handleSearch}
                  disabled={busy}
                  title="Buscar"
                >
                  {loading ? "Buscando..." : "Buscar"}
                </button>
              </div>

              {loading ? (
                <div className="BoxEmpty">Carregando freelancers...</div>
              ) : filtered.length === 0 ? (
                <div className="BoxEmpty">
                  {safeText(search)
                    ? "Nenhum freelancer encontrado para esta busca."
                    : "Nenhum freelancer encontrado."}
                </div>
              ) : (
                <div className="DraftList">
                  {filtered.map((f) => {
                    const selected = selectedFreelancerId === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        className={`DraftItem DraftItemButton ${selected ? "PresenceDirtyRow" : ""}`}
                        onClick={() => setSelectedFreelancerId(f.id)}
                        disabled={saving}
                        title={f.cpf ? `CPF: ${f.cpf}` : undefined}
                      >
                        <div className="DraftInfo">
                          <div className="DraftTitle">{f.nomeCompleto}</div>
                          <div className="DraftSub">{f.cpf ? `CPF: ${f.cpf}` : "CPF não informado"}</div>
                        </div>

                        <div className="SlotBadges">
                          {selected && <span className="SlotBadge SlotBadgeAccent">Selecionado</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="Pager">
              <button
                type="button"
                className="PagerButton PagerButtonLight"
                disabled={loading || saving || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Ant.
              </button>

              <div className="PagerInfo">
                {page} de {totalPages}
              </div>

              <button
                type="button"
                className="PagerButton"
                disabled={loading || saving || page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Próx.
              </button>
            </div>

            <div className="ModalActions">
              <button
                type="button"
                className="ModalButton ModalButtonCancel"
                onClick={onClose}
                disabled={saving}
              >
                Fechar
              </button>

              <button
                type="button"
                className="ModalButton ModalButtonPrimary"
                onClick={handleGoToSlotStep}
                disabled={saving || !selectedFreelancerId}
              >
                Avançar
              </button>
            </div>
          </div>
        )}

        {step === "SLOT" && (
          <div className="ModalBody">
            <div className="SelectedInfoBox">
              <div className="SelectedInfoRow">
                <span className="SelectedInfoLabel">Atual</span>
                <span className="SelectedInfoValue">
                  {dayYmd ? `${formatBrDate(dayYmd)} • ${slotLabel}` : slotLabel}
                </span>
              </div>

              <div className="SelectedInfoRow">
                <span className="SelectedInfoLabel">Novo freelancer</span>
                <span className="SelectedInfoValue">
                  {selectedFreelancerName ? selectedFreelancerName : selectedFreelancerId}
                </span>
              </div>
            </div>

            <div className="Box">
              <div className="BoxTitle">Selecione o horário de destino</div>

              <div className="PresenceValuesTopRow">
                <button
                  type="button"
                  className={`PagerButton ${slotMode === "EXISTING" ? "" : "PagerButtonLight"}`}
                  onClick={() => setSlotMode("EXISTING")}
                  disabled={busy}
                >
                  Escolher horário existente
                </button>

                <button
                  type="button"
                  className={`PagerButton ${slotMode === "CREATE" ? "" : "PagerButtonLight"}`}
                  onClick={() => setSlotMode("CREATE")}
                  disabled={busy}
                >
                  Criar novo horário
                </button>
              </div>

              {slotMode === "EXISTING" && (
                <>
                  {slotsLoading ? (
                    <div className="BoxEmpty">Carregando horários...</div>
                  ) : slotItems.length === 0 ? (
                    <div className="BoxEmpty">Nenhum horário encontrado para este dia.</div>
                  ) : (
                    <div className="DraftList">
                      {slotItems.map((slot) => {
                        const selected = selectedTargetSlotId === slot.id;
                        const isCurrent = slot.id === currentSlotId;
                        const label = `${formatTimeFromIso(slot.startAt)}-${formatTimeFromIso(slot.endAt)}`;

                        return (
                          <button
                            key={slot.id}
                            type="button"
                            className={`DraftItem DraftItemButton ${selected ? "PresenceDirtyRow" : ""}`}
                            onClick={() => setSelectedTargetSlotId(slot.id)}
                            disabled={saving}
                          >
                            <div className="DraftInfo">
                              <div className="DraftTitle">{label}</div>
                              <div className="DraftSub">
                                {slot.available} vaga(s) livre(s) • capacidade total {slot.totalCapacity}
                              </div>
                            </div>

                            <div className="SlotBadges">
                              {isCurrent && <span className="SlotBadge">Atual</span>}
                              {slot.isCanceled && <span className="SlotBadge">Cancelado</span>}
                              {slot.isFull && <span className="SlotBadge">Sem vagas</span>}
                              {selected && <span className="SlotBadge SlotBadgeAccent">Selecionado</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="PVHint" style={{ marginTop: 10 }}>
                    Se o horário escolhido já estiver cheio, o sistema cria mais uma vaga automaticamente.
                  </div>
                </>
              )}

              {slotMode === "CREATE" && (
                <>
                  <div className="BoxTitle" style={{ marginTop: 14 }}>
                    Novo horário (dia: <b>{dayYmd ? formatBrDate(dayYmd) : "-"}</b>)
                  </div>

                  <div className="SlotFormRow">
                    <div className="SlotField">
                      <label className="SlotLabel">Início</label>
                      <input
                        className="SlotInput"
                        type="time"
                        step={1800}
                        value={newStartTime}
                        onChange={(e) => {
                          const nextStart = e.target.value;
                          setNewStartTime(nextStart);
                          setNewEndTime(hhmmFromMinutes(minutesFromHHMM(nextStart) + 9 * 60));
                        }}
                        disabled={busy}
                      />
                    </div>

                    <div className="SlotField">
                      <label className="SlotLabel">Fim</label>
                      <input
                        className="SlotInput"
                        type="time"
                        step={1800}
                        value={newEndTime}
                        onChange={(e) => setNewEndTime(e.target.value)}
                        disabled={busy}
                      />
                    </div>
                  </div>

                  <div className="PVHint" style={{ marginTop: 10 }}>
                    Se já existir horário com o mesmo intervalo, o sistema aumenta uma vaga nele.
                  </div>
                </>
              )}
            </div>

            <div className="SelectedInfoBox">
              <div className="SelectedInfoRow">
                <span className="SelectedInfoLabel">Destino</span>
                <span className="SelectedInfoValue">
                  {slotMode === "EXISTING"
                    ? selectedTargetSlot
                      ? `${formatTimeFromIso(selectedTargetSlot.startAt)}-${formatTimeFromIso(selectedTargetSlot.endAt)}`
                      : "-"
                    : `${newStartTime}-${newEndTime}`}
                </span>
              </div>
            </div>

            <div className="ModalActions">
              <button
                type="button"
                className="ModalButton ModalButtonCancel"
                onClick={() => setStep("SELECT")}
                disabled={saving}
              >
                Voltar
              </button>

              <button
                type="button"
                className="ModalButton ModalButtonPrimary"
                onClick={handleConfirm}
                disabled={
                  saving ||
                  !selectedFreelancerId ||
                  (slotMode === "EXISTING" ? !selectedTargetSlotId : !dayYmd)
                }
              >
                {saving ? "Salvando..." : "Confirmar substituição"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubstituteFreelancerModal;
